// src/pages/MultiStepCheckout.js
import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Box, Button, Heading, useToast, VStack } from "@chakra-ui/react";
import { useCheckout } from "./CheckoutContext";
import ShippingStep from "./steps/ShippingStep";
import PaymentStep from "./steps/PaymentStep";
import ConfirmationStep from "./steps/ConfirmationStep";

const MultiStepCheckout = () => {
  const toast = useToast();
  const methods = useForm({
    defaultValues: {
      shipping: {
        name: "",
        address: "",
        zip: "",
        city: "",
        country: "",
      },
      payment: {
        cardNumber: "",
        expiry: "",
        cvc: "",
      },
    },
  });

  // Henter data fra Context for å evt. synkronisere med global state
  const { setCheckoutData } = useCheckout();

  // State for nåværende steg
  const [currentStep, setCurrentStep] = useState(1);

  const totalSteps = 3;

  const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  // Funksjon for å håndtere neste-knapp
  const handleNext = (data) => {
    // Oppdater CheckoutContext med formdata
    setCheckoutData((prev) => ({
      ...prev,
      ...data, // data har struktur { shipping: {...}, payment: {...} }
    }));
    nextStep();
  };

  // Funksjon for å håndtere fullføring av bestilling
  const handleFinish = (data) => {
    setCheckoutData((prev) => ({
      ...prev,
      ...data,
    }));
    toast({
      title: "Bestilling fullført!",
      description: "Takk for din bestilling. Du vil motta en bekreftelse på e-post.",
      status: "success",
      duration: 4000,
      isClosable: true,
    });
    // Evt. logikk for å sende data til server / betalingstjeneste
  };

  // Avhengig av hvilket steg vi er på, viser vi riktig komponent
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <ShippingStep />;
      case 2:
        return <PaymentStep />;
      case 3:
        return <ConfirmationStep />;
      default:
        return null;
    }
  };

  // Avhengig av steget velger vi hvilken onSubmit-funksjon som skal kjøres
  const onSubmit = (data) => {
    if (currentStep < totalSteps) {
      handleNext(data);
    } else {
      handleFinish(data);
    }
  };

  return (
    <Box maxW="800px" mx="auto" p={8}>
      <Heading mb={6}>Checkout – steg {currentStep} av {totalSteps}</Heading>

      {/* FormProvider gjør at alle child-komponenter kan bruke react-hook-form */}
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          {renderStep()}

          <VStack mt={6} spacing={4} align="flex-end">
            {currentStep > 1 && (
              <Button variant="outline" onClick={prevStep}>
                Forrige
              </Button>
            )}
            <Button colorScheme="teal" type="submit">
              {currentStep === totalSteps ? "Fullfør bestilling" : "Neste"}
            </Button>
          </VStack>
        </form>
      </FormProvider>
    </Box>
  );
};

export default MultiStepCheckout;